import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Box,
  Alert,
  CircularProgress,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useForm,
  DefaultValues,
  FieldValues,
  FieldErrors,
  Control,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ZodType } from "zod";

interface EntityFormDialogProps<T extends FieldValues> {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: T) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: ZodType<T, any, any>;
  defaultValues: DefaultValues<T>;
  renderFields: (control: Control<T>, errors: FieldErrors<T>) => React.ReactNode;
  submitLabel?: string;
  auditHref?: string;
  warningAlert?: string;
  errorAlert?: string;
}

export function EntityFormDialog<T extends FieldValues>({
  title,
  open,
  onClose,
  onSubmit,
  schema,
  defaultValues,
  renderFields,
  submitLabel,
  auditHref,
  warningAlert,
  errorAlert,
}: EntityFormDialogProps<T>) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, defaultValues, reset]);

  const submitting = isSubmitting;

  const onFormSubmit = handleSubmit(async (data) => {
    await onSubmit(data);
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="entity-form-dialog-title"
    >
      <DialogTitle id="entity-form-dialog-title" sx={{ pr: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="Close dialog"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={onFormSubmit} noValidate>
        <DialogContent>
          {warningAlert && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warningAlert}
            </Alert>
          )}
          {errorAlert && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorAlert}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {renderFields(control, errors)}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
          <Box>
            {auditHref && (
              <Link
                href={auditHref}
                variant="caption"
                color="var(--c-link-accent)"
                underline="hover"
              >
                View audit history →
              </Link>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {submitting ? "Saving..." : (submitLabel ?? "Save Changes")}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EntityFormDialog;
